import React, { useState, useRef } from 'react';
import {
  Box, Button, Input, Text, VStack, HStack, useToast, Flex,
  Image, Badge, Divider, Icon, PinInput, PinInputField,
} from '@chakra-ui/react';
import { MdShield, MdArrowBack } from 'react-icons/md';
import { motion } from 'framer-motion';
import axiosInstance from 'api/AxiosInstance';
import { useDispatch } from 'react-redux';
import { loginUser } from '../../redux/slices/authSlice';
import logo from '../../assets/img/profile/logo.png';

const MotionBox = motion(Box);
const DOT_PATTERN = `url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='2' cy='2' r='1.5' fill='rgba(215,163,75,0.12)'/%3E%3C/svg%3E")`;

interface Props {
  tempToken: string;
  onSuccess: () => void;
  onBack?: () => void;
}

const TwoFactorPrompt: React.FC<Props> = ({ tempToken, onSuccess, onBack }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const dispatch = useDispatch();

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast({ title: 'Ingresa los 6 dígitos del código', status: 'warning', duration: 2500 });
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post('/2fa/verificar', {
        token: code,
        tempToken,
      });

      if (response.data?.accessToken) {
        dispatch(loginUser({
          id: response.data.user.id,
          name: response.data.user.name,
          email: response.data.user.email,
          rol: response.data.user.rol,
        }));
        document.cookie = `sesion_t=${response.data.accessToken}; path=/`;
        onSuccess();
      } else {
        throw new Error();
      }
    } catch {
      toast({
        title: 'Código inválido',
        description: 'Verifica tu app de autenticación e intenta de nuevo.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      position="relative"
      overflow="hidden"
      bg="#3a0e1c"
    >
      <Box position="absolute" inset={0} bgGradient="linear(135deg, #3a0e1c 0%, #5b182b 45%, #7b1f35 100%)" />
      <Box position="absolute" inset={0} backgroundImage={DOT_PATTERN} backgroundSize="24px 24px" opacity={0.6} />
      <Box position="absolute" top="-10%" right="-5%" w="500px" h="500px" borderRadius="full" bg="rgba(215,163,75,0.06)" filter="blur(80px)" />

      <MotionBox
        w={{ base: '92%', sm: '420px' }}
        bg="white"
        borderRadius="2xl"
        boxShadow="0 25px 60px rgba(0,0,0,0.5)"
        overflow="hidden"
        zIndex={2}
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 } as any}
      >
        {/* Header */}
        <Box
          bgGradient="linear(to-br, #5b182b, #5b182b)"
          px={8}
          pt={7}
          pb={5}
          textAlign="center"
          position="relative"
        >
          <Box position="absolute" top={0} left={0} right={0} h="3px" bg="linear-gradient(90deg, transparent, #d7a34b, transparent)" />

          <Box w="64px" h="64px" mx="auto" mb={3} borderRadius="full" bg="white" p="5px" boxShadow="0 4px 16px rgba(0,0,0,0.3)">
            <Image src={logo} alt="Totolac" w="100%" h="100%" objectFit="contain" borderRadius="full" />
          </Box>

          <HStack justify="center" spacing={2} mb={1}>
            <Icon as={MdShield} color="#d7a34b" boxSize={4} />
            <Text fontSize="sm" fontWeight="bold" color="white" letterSpacing="0.05em">
              Verificación en dos pasos
            </Text>
          </HStack>
          <Text fontSize="xs" color="rgba(255,255,255,0.65)">
            H. Ayuntamiento de Totolac · 2025-2028
          </Text>
        </Box>

        {/* Cuerpo */}
        <VStack spacing={5} px={8} py={6}>
          <VStack spacing={1} textAlign="center">
            <Text fontSize="sm" color="gray.600" lineHeight="1.6">
              Introduce el código de 6 dígitos generado por tu app de autenticación
            </Text>
            <Badge colorScheme="orange" variant="subtle" fontSize="xs">
              Google Authenticator · Microsoft Authenticator
            </Badge>
          </VStack>

          {/* PIN input de 6 dígitos */}
          <HStack justify="center" spacing={2}>
            <PinInput
              size="lg"
              value={code}
              onChange={setCode}
              onComplete={handleVerify}
              otp
              placeholder="○"
            >
              {[0, 1, 2, 3, 4, 5].map(i => (
                <PinInputField
                  key={i}
                  fontWeight="bold"
                  fontSize="xl"
                  borderColor="gray.300"
                  _focus={{ borderColor: '#5b182b', boxShadow: '0 0 0 1px #5b182b' }}
                  borderRadius="lg"
                  w="44px"
                  h="48px"
                />
              ))}
            </PinInput>
          </HStack>

          <Button
            w="100%"
            bg="#5b182b"
            color="white"
            _hover={{ bg: '#8b1f33', transform: 'translateY(-1px)', boxShadow: 'lg' }}
            _active={{ bg: '#7b1f35', transform: 'translateY(0)' }}
            transition="all 0.2s"
            onClick={handleVerify}
            isLoading={loading}
            loadingText="Verificando..."
            isDisabled={code.length !== 6}
            borderRadius="lg"
            fontWeight="bold"
          >
            Verificar código
          </Button>
        </VStack>

        <Divider />
        <Flex justify="center" px={8} py={3} bg="gray.50">
          <Text fontSize="xs" color="gray.400" textAlign="center">
            El código se renueva cada 30 segundos.{' '}
            {onBack && (
              <Text
                as="span"
                color="#5b182b"
                cursor="pointer"
                fontWeight="semibold"
                _hover={{ textDecoration: 'underline' }}
                onClick={onBack}
              >
                ← Volver al inicio de sesión
              </Text>
            )}
          </Text>
        </Flex>
      </MotionBox>
    </Flex>
  );
};

export default TwoFactorPrompt;
